"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { FiX, FiMail, FiMessageSquare, FiType, FiFileText } from "react-icons/fi"
import Loader from "../../utils/loader"
import toast from "react-hot-toast"
import api from "../../libs/axios"

enum TemplateType {
  EMAIL = "email",
  SMS = "sms",
  PUSH = "push",
}

enum TemplateStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  DRAFT = "draft",
}

interface EditTemplateOffcanvasProps {
  templateId: string
  showEditTemplateCanvas: boolean
  setShowEditTemplateCanvas: (show: boolean) => void
  onTemplateUpdated: (template: any) => void
}

interface TemplateFormData {
  name: string
  subject: string
  message: string
  type: TemplateType
  status: TemplateStatus
}

const EditTemplateOffcanvas: React.FC<EditTemplateOffcanvasProps> = ({
  templateId,
  showEditTemplateCanvas,
  setShowEditTemplateCanvas,
  onTemplateUpdated,
}) => {
  const [formData, setFormData] = useState<TemplateFormData>({
    name: "",
    subject: "",
    message: "",
    type: TemplateType.EMAIL,
    status: TemplateStatus.DRAFT,
  })

  const [errors, setErrors] = useState<Partial<TemplateFormData>>({})
  const [loading, setLoading] = useState(false)

  // Fetch template data when component mounts or templateId changes
  useEffect(() => {
    if (templateId && showEditTemplateCanvas) {
      setLoading(true)
      ;(async () => {
        try {
          const res = await api.get(`/notifications/templates/${templateId}`)
          const t = res.data?.data?.template || res.data?.template
          if (t) {
            setFormData({
              name: t.name || "",
              subject: t.subject || "",
              message: t.message || "",
              type: (t.type || TemplateType.EMAIL) as TemplateType,
              status: (t.status || TemplateStatus.DRAFT) as TemplateStatus,
            })
          }
        } catch (e) {
          // fallback: keep defaults
        } finally {
          setLoading(false)
        }
      })()
    }
  }, [templateId, showEditTemplateCanvas])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    // Clear error when user starts typing
    if (errors[name as keyof TemplateFormData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<TemplateFormData> = {}

    // Template Name validation (required, min 3 characters)
    if (!formData.name.trim()) {
      newErrors.name = "Template name is required"
    } else if (formData.name.trim().length < 3) {
      newErrors.name = "Template name must be at least 3 characters"
    }

    // Subject validation (required for email and push)
    if ((formData.type === TemplateType.EMAIL || formData.type === TemplateType.PUSH) && !formData.subject.trim()) {
      newErrors.subject = "Subject is required for email and push notifications"
    }

    // Message validation (required, min 10 characters)
    if (!formData.message.trim()) {
      newErrors.message = "Message is required"
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        const payload: any = {
          name: formData.name,
          subject: formData.subject || null,
          message: formData.message,
          type: formData.type,
          status: formData.status,
        }
        const res = await api.put(`/notifications/templates/${templateId}`, payload)
        const updated = res.data?.data?.template || { id: Number.parseInt(templateId), ...formData }
        toast.success("Template updated successfully")
        onTemplateUpdated(updated)
        setErrors({})
        setShowEditTemplateCanvas(false)
      } catch (e: any) {
        toast.error(e?.response?.data?.message || "Failed to update template")
      }
    } else {
      toast.error("Please fix the validation errors before submitting")
    }
  }

  const handleClose = () => {
    setShowEditTemplateCanvas(false)
    setErrors({})
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
          showEditTemplateCanvas ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleClose}
      />

      {/* Offcanvas */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
          showEditTemplateCanvas ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <FiFileText className="mr-2" />
            Edit Template
          </h2>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Loading State */}
        {loading && <Loader />}

        {/* Form */}
        {!loading && (
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FiType className="inline mr-1" />
                    Template Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.name ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter template name"
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value={TemplateType.EMAIL}>Email</option>
                      <option value={TemplateType.SMS}>SMS</option>
                      <option value={TemplateType.PUSH}>Push Notification</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value={TemplateStatus.DRAFT}>Draft</option>
                      <option value={TemplateStatus.ACTIVE}>Active</option>
                      <option value={TemplateStatus.INACTIVE}>Inactive</option>
                    </select>
                  </div>
                </div>

                {(formData.type === TemplateType.EMAIL || formData.type === TemplateType.PUSH) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <FiMail className="inline mr-1" />
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.subject ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter subject line"
                    />
                    {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <FiMessageSquare className="inline mr-1" />
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={8}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.message ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter your message content. You can use variables like {{name}}, {{email}}, etc."
                  />
                  {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                  <p className="text-xs text-gray-500 mt-1">
                    Tip: Use variables like {`{{name}}`}, {`{{email}}`}, {`{{order_id}}`} to personalize messages
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-6 mb-20">
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors font-medium"
                >
                  Update Template
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default EditTemplateOffcanvas
